class MappingUtils {
  static getPolylineCentroid(points) {
    const totalDistance = MappingUtils.getPolylineLength(points);
    return MappingUtils.centerPoint(points, totalDistance);
  }

  static getPolylineLength(points) {
    let distance = 0;

    points.forEach((point, index) => {
      if (typeof points[index + 1] === 'undefined') return;
      distance += point.euclideanDistanceFrom(points[index + 1]);
    });

    return distance;
  }

  static centerPoint(points, totalDistance) {
    const distanceToMiddlePoint = totalDistance / 2;

    if (distanceToMiddlePoint === 0) {
      return points[0];
    }

    if (distanceToMiddlePoint < 0 || points.length < 2) {
      return null;
    }

    let distanceToNextPoint = 0;
    let distanceToPreviousPoint = 0;
    let pointIndex = 0;

    for (let i = 1; i <= points.length; i += 1) {
      distanceToPreviousPoint = distanceToNextPoint;

      distanceToNextPoint += points[i].euclideanDistanceFrom(points[i - 1]);

      if (distanceToNextPoint >= distanceToMiddlePoint) {
        pointIndex = i;
        break;
      }
    }

    if (distanceToNextPoint < distanceToMiddlePoint) {
      return null;
    }

    const previousPoint = points[pointIndex - 1];
    const nextPoint = points[pointIndex];
    const vectorScalingFactor = (distanceToMiddlePoint - distanceToPreviousPoint) / (distanceToNextPoint - distanceToPreviousPoint);
    const x = previousPoint.getX() + (nextPoint.getX() - previousPoint.getX()) * vectorScalingFactor;
    const y = previousPoint.getY() + (nextPoint.getY() - previousPoint.getY()) * vectorScalingFactor;

    return {
      coordinate: new IWCoordinate(x, y, IWCoordinate.WGS84),
      angle: MappingUtils.getAngle(points[pointIndex - 1], points[pointIndex]),
    };
  }

  /**
   * Formula for bearing on a rhumb line (https://en.wikipedia.org/wiki/Rhumb_line):
   *
   * Δψ = ln( tan(π/4 + φ2/2) / tan(π/4 + φ1/2) ) ----> ("projected" latitude difference)
   * θ = atan2(Δλ, Δψ)
   * - where φ is latitude, λ is longitude, Δλ is taking shortest route (<180°),
   * R is the earth’s radius, ln is natural log
   *
   * @param cord1
   * @param cord2
   * @returns {number}
   * @static
   */
  static getAngle(cord1, cord2) {
    const toRadians = function (value) {
      return (value * Math.PI) / 180;
    };

    const toDegrees = function (radians) {
      return (radians * 180) / Math.PI;
    };

    const pointA = cord1.toWGS84();
    const pointB = cord2.toWGS84();

    let delta = toRadians((pointB.getX() - pointA.getX()));

    // if delta over 180° take shorter rhumb line across the anti-meridian
    if (delta > Math.PI) delta -= 2 * Math.PI;
    if (delta < -Math.PI) delta += 2 * Math.PI;

    const log = Math.log(
      Math.tan((toRadians(pointB.getY()) / 2) + (Math.PI / 4))
      / Math.tan((toRadians(pointA.getY()) / 2) + (Math.PI / 4)),
    );

    const theta = Math.atan2(delta, log);

    return (toDegrees(theta) + 360) % 360;
  }

  static getFormattedAddressName(address) {
    let formattedAddress1 = [`${address.getStreet()} ${address.getHouseNumber()}`];
    let formattedAddress2 = [address.getPart(), address.getZipCode(), address.getCity()];
    formattedAddress1 = formattedAddress1.filter(value => !!value && (value !== ' '));
    formattedAddress2 = formattedAddress2.filter(value => !!value && (value !== ' '));

    return `${formattedAddress1.join(', ')}<br /><em>${formattedAddress2.join(', ')}</em>`;
  }
}

module.exports = MappingUtils;
